import React from 'react';
import { graphql } from 'gatsby';
import Img from 'gatsby-image';
import BackgroundImage from 'gatsby-background-image';

import LogoSvg from '../assets/logo-black.svg';
import Layout from '../components/layout';
import Columns from '../components/columns';
import SEO from '../components/seo';
import Header from '../components/header';
import Main from '../components/main';
import { LinkAsButton } from '../components/button';
import Text, { HeaderText2, Label, SubLabel } from '../components/text';
import PageWrapper from '../components/pageWrapper';
import Hero from '../components/hero';
import rem from '../utils/rem';
import flex from '../styles/flex';
import Pulse from '../components/pulse';
import { breakpoints, smaller } from '../styles/responsive';
import TabContext from '@material-ui/lab/TabContext';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Box from '@material-ui/core/Box';

import { makeStyles } from '@material-ui/core/styles';

import Button from '@material-ui/core/Button';

import PDF_quick_reference_guide from '../resources/documents/quick-reference-guide_en.pdf';
import PDF_product_description_en from '../resources/documents/product-description_en.pdf';
import PDF_product_description_es from '../resources/documents/product-description_es.pdf';
import PDF_user_manual_en from '../resources/documents/user-manual_en.pdf';
import PDF_user_manual_es from '../resources/documents/user-manual_es.pdf';

import ParameterValueTable from '../components/parameterValueTable.js';
import AEnvelopeFirmwareUpdates from '../containers/a-envelopeFirmwareUpdates.js';
import AEnvelopeImages from '../containers/a-envelopeImages.js';
import AEnvelopeVideos from '../containers/a-envelopeVideos.js';
import AEnvelopeRetailers from '../containers/a-envelopeRetailers.js';
import ExternalLink from '../components/externalLink.js';

import smallBatch from '../images/a-envelope/small-batch.jpg';
import modularGrid from '../images/a-envelope/modgrid_dark.svg';

import { CustomVideoDetail } from '../components/videoList/videoDetail';


function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div {...other}>
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

function createData(name, value) {
  return { name, value };
}

function createDocument(name, content, downloadName) {
  return { name, content, downloadName };
}

function DownloadButton({ document }) {

  return (
    <Button
      href={document.content}
      download={document.downloadName}
      variant="outlined"
      rel="noopener noreferrer"
      style={{ display: "table-cell" }}>
      {document.name}
    </Button>
  );
}

function DownloadButtonContainer(props) {

  const useStyles = makeStyles({
    root: {
      display: 'flex',
      alignItems: 'flex-start',
      flexDirection: 'column',
      '& > *': {
        margin: `${rem(5)}`
      },
      ...flex.leftVertical
    },
  });

  const classes = useStyles();

  return (<div className={classes.root}>

    {props.children}

  </div>)
}

function FirmwareUpdateVideo() {

  const useStyles = makeStyles({
    root: {
      marginBottom: 45,
    },
    media: {
      height: 400,
    },
  });

  const styles = useStyles();

  return <CustomVideoDetail video={{ id: 'F216mZKSYvw', title: 'Firmware Update', description: 'Firmware Update procedure' }} styles={styles} />
}

const AEnvelopePage = ({ data }) => {

  // Tab state.
  const [value, setValue] = React.useState(0);
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };
  return (
    <Layout>
      <SEO
        title="A-envelope | Velectronic | Eurorack | Modular | Synth"
        keywords={[
          `velectronic`,

          `a-envelope`,
          `eurorack`,
          `modular`,

          `synth`,
          `synthesizer`,
          `electronics`,

          `sound`,
          `audio`,

          `spain`,
          `barcelona`,
          `sabadell`,
          `castellar del valles`,
        ]}
      />
      <Header siteTitle={data.site.siteMetadata.title}>
        <div
          style={{
            background: "radial-gradient(#6E7D84,#7D8E97,#283B46)",
          }}
        >
          <Hero type="odd" inline color="#fff" nextId="module-content">
            <PageWrapper style={{ ...flex.vertical, ...flex.centerVertical }}>
              <div
                css={{
                  maxWidth: rem(600),
                  width: "100%",
                  height: "100%",
                  boxShadow: "0 20px 40px 0 rgba(0,0,0,0.27)",
                  ...flex.vertical,
                  ...flex.centerVertical,
                  marginTop: rem(75),
                  [smaller(breakpoints.xlg)]: {
                    maxWidth: rem(425),
                  },
                  [smaller(breakpoints.lg)]: {
                    maxWidth: rem(375),
                  },
                  [smaller(breakpoints.sm)]: {
                    maxWidth: rem(325),
                  },
                }}
              >
                <Pulse start={0.1} animation="pulse2" />
                <Pulse start={0.5} animation="pulse1" />
                <Pulse start={0.2} animation="pulse3" />
                <Pulse start={0} animation="pulse4" />
                <Pulse start={0.7} animation="pulse5" />
                <div style={{ width: "100%", height: "100%" }}>
                  <Img fluid={data.module.childImageSharp.fluid} alt="A-envelope" />
                </div>
              </div>
            </PageWrapper>
          </Hero>
        </div>
      </Header>
      <Main>
        <section
          id="module-content"
          css={{
            marginBottom: rem(10),
            [smaller(breakpoints.sm)]: {
              marginBottom: rem(5),
            },
          }}
        >
          <PageWrapper style={{
            ...flex.vertical,
            paddingTop: `${rem(100)}`,
          }}>
            <header>
              <HeaderText2>
                Associative Quadruple Envelope
              </HeaderText2>
            </header>
            <TabContext value='{value}' style={{ zIndex: `${10}` }}>
              <Tabs value={value} onChange={handleChange}

                variant="scrollable"
                scrollButtons={true}
                allowScrollButtonsMobile

              >
                <Tab label="Description" />
                <Tab label="Features" />
                <Tab label="Characteristics" />
                <Tab label="Downloads" />
                <Tab label="Videos" />
              </Tabs>
              <TabPanel value={value} index={0}>
                <PageWrapper style={{ ...flex.vertical }}>
                  <Columns count="2" style={{ padding: `${rem(50)} 0` }}>
                    <Label>Why the A-envelope</Label>
                    <Text>
                      A-envelope was created in its basis to help and simplify the patching in polyphonic configurations.
                      As we all can imagine, bringing polyphony to modular is a pretty tough thing to do because a good bunch of reasons like, space, dedicated modules, a lot of boilerplate patching and more, and even if sometimes we would like to have an harmonic duet or a three or four notes chord, we end up looking for wacky "workarounds".
                      Additionally, during the iterative ideation and creation process some other interesting ideas came up and we could not hold us back from adding them.
                    </Text>
                    <Text>
                      Of course and as usual, every one of us will imagine very different application depending on our own use-case.
                    </Text>
                    <br />
                    <Label>In what consists</Label>
                    <Text>
                      A-envelope is basically a four channel ADSR envelope with the capability of associating these channels in groups for instance to create polyphonyc patches.
                      Each of the channels has also modulation capabilities up to audio rate frequencies for every of the parameters of the envelope. Moreover allows configurable begin and end of stage outputs to easily chain events or states to create complex movements.
                    </Text>
                    <Label>Easy to use</Label>
                    <Text>
                      The A-envelope despite it is a highly configurable and complete element, its user interface is been made for permorfing. Has LED monitors for every move.
                    </Text>
                  </Columns>
                  <img src={smallBatch} alt={"small batch"} />

                  <div
                    css={{
                      margin: `0 auto`,
                      padding: `${rem(15)} 0`,
                      ...flex.horizontal,
                      ...flex.spaceBetween,
                      [smaller(breakpoints.sm)]: {
                        ...flex.vertical,
                      },
                    }}
                  >
                    <ExternalLink href="https://www.modulargrid.net/e/velectronic-a-envelope">
                      <img src={modularGrid} alt={"See it in Modular Grid"} />
                    </ExternalLink>
                  </div>
                </PageWrapper>
              </TabPanel>
              <TabPanel value={value} index={1}>
                <PageWrapper style={{ ...flex.vertical }}>
                  <Label>A closer view</Label>
                  <AEnvelopeImages />
                  <Columns count="1" style={{ padding: `${rem(50)} 0` }}>
                    <Label>Association between channels</Label>
                    <Text>
                      A-envelope allows to define channel groups, that shares configuration.
                      This capability makes possible to define polyphonic configurations on the go up to 4 voices, in a simple way.
                    </Text>
                    <Label>Each channel allows to</Label>
                    <Text tag='ul'>
                      <li>Modulate every of the ADSR stages in time or shape (Exp, Lin, Log) independently.</li>
                      <li>Adjust the amount of modulation using the stage knobs when acting as attenuverters.</li>
                      <li>Controlling the duration of each stage from 0 to 20 seconds using the stage knobs.</li>
                      <li>Controlling the shape of attack, decay and release stages.</li>
                      <li>Controlling the level of the sustain stage.</li>
                      <li>Controlling the amplitude of the whole envelope.</li>
                      <li>Configure the input activation mode as Gate, Trigger or Re-Trigger.</li>
                      <li>Configure the channel in single, loop or auto-loop mode.</li>
                      <li>Configure the BEOS (Begin and/or End Of Stage) outputs for each stage.</li>
                      <li>Trigger the channel manually using the test button.</li>
                    </Text>
                    <Label>Loop & Auto Loop</Label>
                    <Text>
                      If the Gate mode is activated, the envelope will be
                      automatically relayed at the end while the input signal remains
                      activated.
                    </Text>
                    <Text>
                      If the Trigger mode is activated, the envelope will
                      automatically re-launch when it finishes after receiving a
                      pulse. Receiving a new pulse will stop the loop.
                    </Text>
                    <Label>User interface</Label>
                    <Text>
                      The user interface is meant to give a fast and direct access to every feature.
                    </Text>
                    <Text tag='ul'>
                      <li>Instant visualization of each channel group thru the scale indicators and LED arches.</li>
                      <li>Direct monitoring of the signal and BEOS outputs thru specific LEDs.</li>
                    </Text>
                    <Label>Pulse / Gate</Label>
                    <Text>Selection of the entries work mode.</Text>
                    <Label>Input Jacks</Label>
                    <Text>Selection of the entries work mode.</Text>
                    <Label>Input Buttons</Label>
                    <Text>Trigger / gate buttons for the envelope manual start</Text>
                  </Columns>
                </PageWrapper>
              </TabPanel>
              <TabPanel value={value} index={2}>
                <PageWrapper style={{ ...flex.vertical }}>
                  <Columns count="1" style={{ padding: `${rem(50)} 0` }}>
                    <Label>Time ranges</Label>
                    <Text tag='div' style={{ padding: `0 0 0 ${rem(50)}` }}>
                      <SubLabel>Attack</SubLabel>
                      <Text>
                        The minimum amount of time is 400 microseconds, under this value the stage is omitted.
                        The maximum amount of time is 20 seconds.
                      </Text>
                      <SubLabel>Decay</SubLabel>
                      <Text>
                        The minimum amount of time is 400 microseconds, under this value the stage is omitted
                        and therefore, the maximum voltage for the attack becomes the one defined by the sustain.
                        The maximum amount of time is 20 seconds.
                      </Text>
                      <SubLabel>Sustain</SubLabel>
                      <Text>
                        The minimum amount of time is 400 microseconds, under this value the stage is omitted
                        but the voltage level remains as a reference for the other stages.
                        The maximum amount of time is 20 seconds.
                      </Text>
                      <SubLabel>Release</SubLabel>
                      <Text>
                        The minimum amount of time is 400 microseconds, under this value the stage is omitted
                        The maximum amount of time is 20 seconds.
                      </Text>
                    </Text>
                    <Label>Inputs</Label>
                    <Text tag='div' style={{ padding: `0 0 0 ${rem(50)}` }}>
                      <SubLabel>ADSR modulation inputs</SubLabel>
                      <Text>
                        The ADSR modulation inputs accepts signals from -10 to 10 volts and a bandwidth from DC up to 20kHz.
                        The attenuverters associated to the inputs can amplify/attenuate the input signal from 0 to x2.
                      </Text>
                      <ParameterValueTable rows={[
                        createData('Input impedance', '100kOhms'),
                        createData('Maximum input voltage', '+/- 10 Volts'),
                        createData('Bandwidth', 'DC to 20kHz'),
                      ]}>
                      </ParameterValueTable>
                      <SubLabel>Trigger/Gate inputs</SubLabel>
                      <Text>
                        The trigger/gate inputs react to pulses of at least 2.5Volts and 50us.
                      </Text>
                      <ParameterValueTable rows={[
                        createData('Input impedance', '100kOhms'),
                        createData('Minimum input voltage', '2.5 Volts'),
                        createData('Minimum pulse duration', '50uS'),
                      ]}>
                      </ParameterValueTable>
                    </Text>
                    <Label>Outputs</Label>
                    <Text tag='div' style={{ padding: `0 0 0 ${rem(50)}` }}>
                      <SubLabel>ADSR envelope outputs</SubLabel>
                      <Text>
                        The ADSR envelope outputs have a voltage range of 0-10 volts, and a minimum period time of 400 microseconds using only the Attack stage.
                        The ADSR envelope outputs are doubled to offer both direct and inverted outputs. Both sets have the same characteristics.
                      </Text>
                      <ParameterValueTable rows={[
                        createData('Output impedance', '1kOhms'),
                        createData('Output voltage', '0 - 10 Volts'),
                      ]}>
                      </ParameterValueTable>
                      <SubLabel>The BEOS outputs</SubLabel>
                      <Text>
                        The BEOS (Begin-End Of Stage) outputs have a voltage range of 0 volts when inactive and 10 volts when active.
                      </Text>
                      <ParameterValueTable rows={[
                        createData('Output impedance', '1kOhms'),
                        createData('Output voltage', '0 / 10 Volts'),
                      ]}>
                      </ParameterValueTable>
                    </Text>
                    <Label>Power consumption</Label>
                    <Text>
                      The power consumption it is been measured with all LEDs on.
                    </Text>
                    <Text tag='div' style={{ padding: `0 0 0 ${rem(50)}` }}>
                      <SubLabel>The +5V jumper selected (Recommended)</SubLabel>
                      <Text tag='ul'>
                        <li>+5v = 170 mA</li>
                        <li>+12v = 35 mA</li>
                        <li>-12v = 23 mA</li>
                      </Text>
                      <SubLabel>The +12V jumper selected</SubLabel>
                      <Text tag='ul'>
                        <li>+5v = 0 mA</li>
                        <li>+12v = 245 mA</li>
                        <li>-12v = 23 mA</li>
                      </Text>
                      <SubLabel>Protection</SubLabel>
                      <Text tag='ul'>
                        <li>Protection for human body model electrostatic discharge (ESD) of 2kV.</li>
                        <li>Power connector reverse protection.</li>
                        <li>Protection against power polarity inversion.</li>
                      </Text>
                      <SubLabel>Working Temperature</SubLabel>
                      <Text tag='ul'>
                        <li>Working ambient temperature from -20ºC to 70ºC.</li>
                      </Text>
                    </Text>
                    <Label>Accuracy</Label>
                    <Text tag='ul'>
                      <li>The accuracy for all specified values is &lt; 1%.</li>
                    </Text>
                    <Label>Dimensions</Label>
                    <Text tag='ul'>
                      <li>22HP</li>
                      <li>3U</li>
                      <li>38mm depth from the back of the panel to the power connector, including the connector.</li>
                    </Text>
                  </Columns>
                </PageWrapper>
              </TabPanel>
              <TabPanel value={value} index={3}>
                <PageWrapper style={{ ...flex.vertical }}>
                  <Columns count="1" style={{ padding: `${rem(50)} 0` }}>
                    <Label>Documentation</Label>
                    <Text>
                      Here below you can find all the current available documents, including the one delivered with the module.
                    </Text>
                    <DownloadButtonContainer>
                      <DownloadButton document={createDocument("Quick reference guide", PDF_quick_reference_guide)} />
                    </DownloadButtonContainer>
                    <DownloadButtonContainer>
                      <DownloadButton document={createDocument("Product description", PDF_product_description_en)} />
                      <DownloadButton document={createDocument("Descripción del producto", PDF_product_description_es)} />
                    </DownloadButtonContainer>
                    <DownloadButtonContainer>
                      <DownloadButton document={createDocument("User manual", PDF_user_manual_en)} />
                      <DownloadButton document={createDocument("Manual de usuario", PDF_user_manual_es)} />
                    </DownloadButtonContainer>
                  </Columns>
                </PageWrapper>
                <PageWrapper style={{ ...flex.vertical }}>
                  <Columns count="1" style={{ padding: `${rem(50)} 0` }}>
                    <Label>Firmware updates</Label>
                    <Text>
                      Here below you can find all the available firmware update, including the one delivered with the module.
                    </Text>
                    <AEnvelopeFirmwareUpdates />
                    <Label>Update procedure</Label>
                    <Text>
                      The A-envelope is provided with a USB A port, for firmware update purposes.
                      Follow the steps to perform the update.
                    </Text>
                    <Text tag='ol' style={{ padding: `0 ${rem(10)}` }}>
                      <li>Download from the firmware list the on you want to apply.</li>
                      <li>Copy the downloaded file to at the root of your USB storage device.</li>
                      <li>Rename the copied file to a-envgen.hex.</li>
                      <li>Power off the module.</li>
                      <li>Keep pressed the Attack modulation button.</li>
                      <li>Power on the module.</li>
                      <li>Now the module is in boot mode.</li>
                      <li>The trigger and sustain modulation buttons will get enabled and the channel buttons will blinking waiting for a USB storage device.</li>
                      <li>Plug the USB storage device in the module USB port.</li>
                      <li>If everything is fine, the channel buttons will show the firmware update check progress.</li>
                      <Text tag='ol'>
                        <li>If the firmware is not been detected, the channel buttons will remain blinking and you will need to check the previous steps.</li>
                        <li>If the firmware is not a valid, or is not suitable for this module, the release shape LED will start blinking.</li>
                      </Text>
                      <li>If the firmware update check succeeds, the channel button will turn off and the loop, trigger and sustain modulation buttons will get enabled.</li>
                      <li>Push the loop button to proceed with the update.</li>
                      <li>The channel buttons will show the firmware update progress.</li>
                      <li>Once the update terminates, the module will restart.</li>
                    </Text>
                    <Text>
                      Now you can enjoy the new features.
                    </Text>
                    <Label>Reset to factory values procedure</Label>
                    <Text>
                      Follow the steps to apply the factory default values.
                    </Text>
                    <Text tag='ol' style={{ padding: `0 ${rem(10)}` }}>
                      <li>Power off the module.</li>
                      <li>Keep pressed the Attack modulation button.</li>
                      <li>Power on the module.</li>
                      <li>Now the module is in boot mode.</li>
                      <li>The trigger and sustain modulation buttons will get enabled and the channel buttons will blinking waiting for a USB storage device.</li>
                      <li>Push the sustain modulation button to proceed with the factory default values recall.</li>
                      <li>Push the trigger button to restart the module.</li>
                    </Text>
                    <Text>
                      Now you can start using it again as came out from the box.
                    </Text>
                  </Columns>
                  <FirmwareUpdateVideo />
                </PageWrapper>
              </TabPanel>
              <TabPanel value={value} index={4}>
                <PageWrapper style={{ ...flex.vertical }}>
                  <Columns count="1" style={{ padding: `${rem(10)} 0` }}>
                    <Label>How does it work?</Label>
                    <Text>
                      Here below you can find a serie of videos that shows how this module works.
                    </Text>
                    <AEnvelopeVideos />
                  </Columns>
                </PageWrapper>
              </TabPanel>
            </TabContext>
          </PageWrapper>
        </section>

        <section
          id="module-retailers"
          css={{
            marginBottom: rem(100),
            [smaller(breakpoints.sm)]: {
              marginBottom: rem(25),
            },
          }}
        >
          <AEnvelopeRetailers />
        </section>
        <BackgroundImage
          Tag="section"
          fluid={data.contentImage.childImageSharp.fluid}
          backgroundColor={`#040e18`}
        >
          <PageWrapper
            css={{
              ...flex.vertical,
              ...flex.spaceBetween,
              ...flex.centerVertical,
              minHeight: rem(700),
              textAlign: "center",
              [smaller(breakpoints.sm)]: {
                minHeight: rem(600),
              },
            }}
          >
            <div style={{ marginBottom: rem(65) }}>
              <LogoSvg
                aria-label={data.site.siteMetadata.title}
                style={{ transform: "scale(1.5)" }}
              />
            </div>
            <Text
              tag="h2"
              size="xlg"
              weight="semibold"
              style={{ color: "#fff" }}
            >
              High quality solutions to your technological needs
            </Text>
            <LinkAsButton
              href="tel:+34937142637"
              style={{ marginTop: rem(65) }}
            >
              Know more
            </LinkAsButton>
          </PageWrapper>
        </BackgroundImage>
      </Main>
    </Layout >
  )
}

export default AEnvelopePage

export const aenvelopeQuery = graphql`
  query aenvelopeQuery {
    site {
      siteMetadata {
        title
      }
    }
    headerImage: file(relativePath: { eq: "hero.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 2000) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    module: file(relativePath: { eq: "a-envelope.png" }) {
      childImageSharp {
        fluid(maxWidth: 2000) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    contentImage: file(relativePath: { eq: "machines.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 2000) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
  }
`
